import { IFluidObject } from '@fluidframework/core-interfaces';
import { StylingProvider, ThemeProperties, TextStyles, StyleProperty } from '@fluidx/office-fluid-types';
import { StylingProviderEvents } from '@fluidx/utilities';
import { PostMessageSender, PostMessageHandlers } from '../Shared/PostMessage';
import { SetThemePropertiesMessage, SetTextStylesMessage, Pairing } from '../MessageTypes';
import { EventEmitter } from 'events';

/**
 * Initializes everything needed to handle the StylingProvider interface outside the
 * iframe.
 * @param sender how to send messages to the inside of the iframe
 */
interface HostStylingProvider {
  readonly serializedThemeProperties: [string, string][] | undefined;
  readonly serializedTextStyles: [string, StyleProperty[]][] | undefined;
  onThemeChange(previousThemeCssProperties: ThemeProperties): void;
  onStylesChange(previousTextStyleProperties: TextStyles): void;
}

export function getHostSideStylingProvider(
  scope: IFluidObject,
  sender: PostMessageSender
):
  | {
      component: HostStylingProvider;
      dispose: () => void;
    }
  | undefined {
  const stylingProvider = scope.StylingProvider;
  if (!stylingProvider) {
    return undefined;
  }

  const hostStylingProvider: HostStylingProvider = {
    get serializedThemeProperties(): [string, string][] | undefined {
      // Serialize the current theme properties to send to the iframe via the scope object
      return stylingProvider && stylingProvider.themeProperties
        ? Array.from(stylingProvider.themeProperties.entries())
        : undefined;
    },
    get serializedTextStyles(): [string, StyleProperty[]][] | undefined {
      // Serialize the current text styles to send to the iframe via the scope object
      return stylingProvider && stylingProvider.textStyles
        ? Array.from(stylingProvider.textStyles.entries())
        : undefined;
    },
    onThemeChange(_previousThemeCssProperties: ThemeProperties) {
      // When we receive a theme change from the outside the iframe instance of the StylingProvider
      // serialized the current theme properties and send them to the iframe. The handler on the
      // iframe (e.g. guest) side will update the properties, which will cause the event theme change event
      // to fire for the iframe.
      const themeProperties = stylingProvider.themeProperties
        ? Array.from(stylingProvider.themeProperties.entries())
        : undefined;
      const message: SetThemePropertiesMessage = {
        type: 'SetThemeProperties',
        payload: { properties: themeProperties }
      };
      sender.send(message);
    },
    onStylesChange(_previousTextStyleProperties: TextStyles) {
      // When we receive a text styles change from the outside the iframe instance of the StylingProvider
      // serialized the current styles and send them to the iframe. The handler on the iframe (e.g. guest) side
      // will update the properties, which will cause the event theme change event to fire for the iframe.
      const textStyles = stylingProvider.textStyles ? Array.from(stylingProvider.textStyles.entries()) : undefined;
      const message: SetTextStylesMessage = {
        type: 'SetTextStyles',
        payload: { styles: textStyles }
      };
      sender.send(message);
    }
  };

  // Register event listeners on the outside the iframe instance of the StylingProvider
  // in order to listen for changes to the StylingProvider created by the application.
  stylingProvider.on(StylingProviderEvents.themePropertiesChanged, (previousThemeCssProperties) =>
    hostStylingProvider.onThemeChange(previousThemeCssProperties)
  );
  stylingProvider.on(StylingProviderEvents.textStylesChanged, (previousTextStyleProperties) =>
    hostStylingProvider.onStylesChange(previousTextStyleProperties)
  );

  return {
    component: hostStylingProvider,
    dispose: () => stylingProvider.removeAllListeners()
  };
}

/**
 * Initializes everything needed to handle the StylingProvider interface calls inside the
 * iframe.
 */
class GuestStylingProvider extends EventEmitter implements StylingProvider {
  private _themeProperties: ThemeProperties;
  private _textStyles: TextStyles;
  constructor(
    serializedThemeProperties: [string, string][] | undefined,
    serializedTextStyles: [string, StyleProperty[]][] | undefined
  ) {
    super();

    // Initial values for the theme properties and text styles are from the serialized scope
    // HostStylingProvider.
    if (serializedThemeProperties && serializedThemeProperties.length > 0) {
      this.setThemeProperties(new Map(serializedThemeProperties));
    }
    if (serializedTextStyles && serializedTextStyles.length > 0) {
      this.setTextStyles(new Map(serializedTextStyles));
    }
  }

  public get StylingProvider() {
    return this;
  }

  public get themeProperties(): ThemeProperties {
    // return inside iframe cached version of the properties. They will be updated if
    // PostMessageHandlers for the SetThemeProperties is called by outside of the iframe.
    return this._themeProperties;
  }
  public get textStyles(): TextStyles {
    // return inside iframe cached version of the properties. They will be updated if
    // PostMessageHandlers for the setTextStyles is called by outside of the iframe.
    return this._textStyles;
  }

  public setThemeProperties(properties: ThemeProperties): void {
    // When setThemeProperties is called from outside of the iframe, the outside iframe proxy will
    // post a message and the PostMessageHandlers for the setThemeProperties will call setThemeProperties
    // on inside of the iframe proxy (GuestStylingProvider).

    // Guest (inside iframe) calls to setThemeProperties are not sent up to the host (outside of the iframe),
    // they remain local only. The guest may change their copy of the the theme properties but we don't want to allow
    // the guest to change the host's copy. Only communication between host/guest is from host to guest.
    const previousThemeProperties = this._themeProperties ? new Map(this._themeProperties) : undefined;
    this._themeProperties = properties;
    this.emit(StylingProviderEvents.themePropertiesChanged, previousThemeProperties);
  }

  public setTextStyles(styles: TextStyles): void {
    // When setTextStyles is called from outside of the iframe, the outside iframe proxy will
    // post a message and the PostMessageHandlers for the setTextStyles will call setTextStyles
    // on inside of the iframe proxy (GuestStylingProvider).

    // Guest (inside iframe) calls to setTextStyles are not sent up to the host (outside of the iframe),
    // they remain local only. The guest may change their copy of the the theme properties but we don't want to allow
    // the guest to change the host's copy. Only communication between host/guest is from host to guest.
    const previousStyles = this._textStyles ? new Map(this._textStyles) : undefined;
    this._textStyles = styles;
    this.emit(StylingProviderEvents.textStylesChanged, previousStyles);
  }
}

export function getGuestSideStylingProvider(
  scope: Pairing<IFluidObject>,
  _sender: PostMessageSender
): { StylingProvider: StylingProvider; handlers: PostMessageHandlers<string> } {
  const appStylingProvider = scope ? ((<any>scope.StylingProvider) as HostStylingProvider) : undefined;
  const serializeThemeProperties = appStylingProvider ? appStylingProvider.serializedThemeProperties : undefined;
  const serializeTextStyles = appStylingProvider ? appStylingProvider.serializedTextStyles : undefined;
  const StylingProvider = new GuestStylingProvider(serializeThemeProperties, serializeTextStyles);

  const handlers: PostMessageHandlers<string> = {
    ['SetThemeProperties']: (message) => {
      if (message && message.payload) {
        const params = (message as SetThemePropertiesMessage).payload;
        const themeProperties =
          params.properties && params.properties.length > 0 ? new Map(params.properties) : undefined;
        StylingProvider.setThemeProperties(themeProperties);
      }
    },
    ['SetTextStyles']: (message) => {
      if (message && message.payload) {
        const params = (message as SetTextStylesMessage).payload;
        const styles = params.styles && params.styles.length > 0 ? new Map(params.styles) : undefined;
        StylingProvider.setTextStyles(styles);
      }
    }
  };
  return { StylingProvider, handlers };
}
